import clsx from 'clsx'
import { Fragment, ReactNode } from 'react'
import { HeaderGroup, Row, TableInstance } from 'react-table'
import { IconPaths, IconSize, KTSVG } from '../../../_metronic/helpers'
import { getColumnAriaSort } from './SortableHeader'
import styles from './styles/DataTable.module.css'
import { TableColumnInstance, UseQueryRequestHook } from './tableTypes'
import { TableSkeletonRows } from './TableStates'

type HeaderCellProps<T extends object> = {
  column: TableColumnInstance<T>
  ariaSort?: 'ascending' | 'descending' | 'none'
}

const HeaderCell = <T extends object>({ column, ariaSort }: HeaderCellProps<T>) => {
  const { key, ...headerProps } = column.getHeaderProps()
  return (
    <th
      key={key}
      {...headerProps}
      scope='col'
      aria-sort={column.sortFields?.length ? ariaSort : undefined}
      className={clsx(styles.th, column.align === 'end' && styles.alignEnd)}
      style={{ width: column.cellWidth }}
    >
      {column.render('Header')}
    </th>
  )
}

const SortAwareHeaderCell = <T extends object>({
  column,
  useQueryRequest,
}: HeaderCellProps<T> & { useQueryRequest: UseQueryRequestHook }) => {
  const { state } = useQueryRequest()
  return <HeaderCell column={column} ariaSort={getColumnAriaSort(state, column.sortFields)} />
}

/**
 * Linha expansível: o botão aparece na coluna de id `expand` e o
 * painel abre numa linha de largura total logo abaixo.
 */
export type ModernTableExpansion<T extends object> = {
  isExpanded: (row: Row<T>) => boolean
  onToggle: (row: Row<T>) => void
  render: (row: Row<T>) => ReactNode
  /** Nome do registro para o nome acessível do botão ("Mostrar detalhes de <nome>"). */
  getRowLabel?: (row: T) => string | undefined
}

type ExpandToggleProps = {
  expanded: boolean
  controls: string
  label?: string
  onToggle: () => void
}

const ExpandToggle = ({ expanded, controls, label, onToggle }: ExpandToggleProps) => {
  const action = `${expanded ? 'Ocultar' : 'Mostrar'} detalhes${label ? ` de ${label}` : ''}`
  return (
    <button
      type='button'
      className={styles.expandToggle}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={action}
      title={action}
    >
      <span aria-hidden='true' className={clsx('d-flex', styles.expandIcon)}>
        <KTSVG path={IconPaths.ArrowDown} size={IconSize.ExtraSmall} />
      </span>
    </button>
  )
}

type ModernTableProps<T extends object> = {
  id: string
  instance: TableInstance<T>
  ariaLabel?: string
  isLoading: boolean
  isError?: boolean
  errorState?: ReactNode
  emptyState?: ReactNode
  isRowSelected?: (row: T) => boolean
  useQueryRequest?: UseQueryRequestHook
  skeletonRows?: number
  expansion?: ModernTableExpansion<T>
}

/**
 * Corpo do CustomTable — cabeçalho legível, linhas com separador sólido,
 * larguras fixas por coluna, skeleton no primeiro carregamento e estados vazio/erro.
 */
export const ModernTable = <T extends object>({
  id,
  instance,
  ariaLabel,
  isLoading,
  isError,
  errorState,
  emptyState,
  isRowSelected,
  useQueryRequest,
  skeletonRows = 10,
  expansion,
}: ModernTableProps<T>) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns } =
    instance
  const showSkeleton = isLoading && rows.length === 0
  const showEmpty = !isLoading && !isError && rows.length === 0

  return (
    <>
      <table
        id={id}
        aria-label={ariaLabel}
        aria-busy={isLoading}
        className={styles.table}
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((group: HeaderGroup<T>) => {
            const { key, ...groupProps } = group.getHeaderGroupProps()
            return (
              <tr key={key} {...groupProps}>
                {group.headers.map((column) => {
                  const col = column as TableColumnInstance<T>
                  return useQueryRequest && col.sortFields?.length ? (
                    <SortAwareHeaderCell
                      key={col.id}
                      column={col}
                      useQueryRequest={useQueryRequest}
                    />
                  ) : (
                    <HeaderCell key={col.id} column={col} />
                  )
                })}
              </tr>
            )
          })}
        </thead>
        {!isError && (
          <tbody {...getTableBodyProps()}>
            {showSkeleton ? (
              <TableSkeletonRows
                columns={visibleColumns.length}
                rows={skeletonRows}
                className={styles.td}
              />
            ) : (
              rows.map((row: Row<T>) => {
                prepareRow(row)
                const { key, ...rowProps } = row.getRowProps()
                const expanded = !!expansion?.isExpanded(row)
                const panelId = `${id}-detalhes-${row.id}`
                return (
                  <Fragment key={key}>
                    <tr
                      {...rowProps}
                      className={clsx(
                        styles.row,
                        isRowSelected?.(row.original) && styles.rowSelected,
                        expanded && styles.rowExpanded
                      )}
                    >
                      {row.cells.map((cell) => {
                        const col = cell.column as TableColumnInstance<T>
                        const { key: cellKey, ...cellProps } = cell.getCellProps()
                        return (
                          <td
                            key={cellKey}
                            {...cellProps}
                            data-col={col.id}
                            data-hide-mobile={col.hideOnMobile ? 'true' : undefined}
                            className={clsx(styles.td, col.align === 'end' && styles.alignEnd)}
                          >
                            {expansion && col.id === 'expand' ? (
                              <ExpandToggle
                                expanded={expanded}
                                controls={panelId}
                                label={expansion.getRowLabel?.(row.original)}
                                onToggle={() => expansion.onToggle(row)}
                              />
                            ) : (
                              cell.render('Cell')
                            )}
                          </td>
                        )
                      })}
                    </tr>
                    {expansion && expanded && (
                      <tr className={styles.expandRow}>
                        <td
                          id={panelId}
                          colSpan={visibleColumns.length}
                          className={clsx(styles.td, styles.expandCell)}
                        >
                          {/* Abre com animação de altura (ver .expandReveal no CSS). */}
                          <div className={styles.expandReveal}>
                            <div className={styles.expandRevealInner}>
                              <div className={styles.expandPanel}>{expansion.render(row)}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })
            )}
          </tbody>
        )}
      </table>

      {showSkeleton && (
        <span role='status' className='d-block fs-7 text-gray-700 mt-3'>
          Carregando…
        </span>
      )}
      {isError && errorState}
      {showEmpty && emptyState}
    </>
  )
}
